import * as React from 'react';
import { pluralize } from '@cian/newbuilding-utils';
import { Image, UIText1, UIText2 } from '@cian/ui-kit';
import { numberToPrettyString } from '@cian/utils';

import * as styles from './styles.css';
import { getImageUrl, getInterestRate, isMortgageButtonAvailable, shortenMaxCredit } from './utils';
import { IPromo } from '../../../../types/newbuilding/promos';
import { TitleColumnRight } from '../TitleColumnRight';

interface IMortgageOfferRowProps {
  monthlyPayment: number | null;
  mortgagePartnerLink: string;
  onClick: (promoInfo: IPromo) => void;
  onGetOnlineClick: (promoInfo: IPromo) => void;
  promoInfo: IPromo;
}

export const MortgageOfferRow: React.FC<IMortgageOfferRowProps> = ({
  monthlyPayment,
  mortgagePartnerLink,
  onClick,
  onGetOnlineClick,
  promoInfo,
}) => {
  const { name: promoName, bank, authorName } = promoInfo;
  const { maxCredit, maxLoanTerm } = promoInfo?.mortgageParams || {};
  const { name: bankName } = bank || {};

  const imageUrl = getImageUrl(promoInfo);
  const isTitleRightVisible = isMortgageButtonAvailable(promoInfo);

  const handleClick = React.useCallback(() => {
    onClick(promoInfo);
  }, [promoInfo, onClick]);

  const handleGetOnlineClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onGetOnlineClick(promoInfo);
    },
    [onGetOnlineClick, promoInfo],
  );

  const maxCreditText = maxCredit ? <UIText1>до {shortenMaxCredit(maxCredit)} ₽</UIText1> : '—';
  const maxLoanTermText = maxLoanTerm ? <UIText1>{pluralize(maxLoanTerm, ['год', 'года', 'лет'])}</UIText1> : '—';
  const monthlyPaymentText = monthlyPayment ? <span>от {numberToPrettyString(monthlyPayment)} ₽/мес</span> : '—';

  return (
    <div className={styles['row']} onClick={handleClick} role="button" data-testid="MortgageOfferRow">
      <div className={styles['title-col']}>
        <div className={styles['image-and-title']}>
          <span data-testid="MortgageOfferRowImage" className={styles['image-block']}>
            <Image objectFit="contain" src={imageUrl} height={56} width={56} borderRadius={'50%'} />
          </span>
          <span className={styles['title-block']}>
            <div className={styles['promo-name']}>{promoName}</div>
            <UIText2 color="icon-secondary-default">{bankName || authorName}</UIText2>
          </span>
        </div>
        {isTitleRightVisible && (
          <div className={styles['title-column-right']}>
            <TitleColumnRight
              isGetOnlineButtonAvailable={isMortgageButtonAvailable(promoInfo)}
              mortgagePartnerLink={mortgagePartnerLink}
              onGetOnlineButtonClick={handleGetOnlineClick}
            />
          </div>
        )}
      </div>
      <span className={styles['rate-col']}>
        <UIText1>{getInterestRate(promoInfo)}</UIText1>
      </span>
      <span className={styles['sum-col']}>{maxCreditText}</span>
      <span className={styles['term-col']}>{maxLoanTermText}</span>
      <span className={styles['payment-col']}>
        <UIText1 textAlign="right">{monthlyPaymentText}</UIText1>
      </span>
    </div>
  );
};
